// Operator profile (PX-451) — the signed-in operator's own account: identity
// (inline name edit), the role + the exact permission set it grants, account
// status, and a self-service password change. "Operating on OTHERS" still lives
// under Users; this tab is purely the operator's own account.

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { APIError, request } from '@/lib/api'
import type { ProfileResponse } from '@/types/api'

// "photoshoots.read" → group "photoshoots", action "read". The wildcard `*`
// (admin) is handled separately by the caller.
function groupPermissions(perms: string[]): { resource: string; actions: string[] }[] {
  const map = new Map<string, string[]>()
  for (const p of perms) {
    const dot = p.indexOf('.')
    const resource = dot === -1 ? p : p.slice(0, dot)
    const action = dot === -1 ? p : p.slice(dot + 1)
    const list = map.get(resource) ?? []
    list.push(action)
    map.set(resource, list)
  }
  return [...map.entries()]
    .map(([resource, actions]) => ({ resource, actions: actions.sort() }))
    .sort((a, b) => a.resource.localeCompare(b.resource))
}

export function ProfilePage() {
  const { profile, setProfile, logout } = useAuth()
  if (!profile) return null

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">My profile</h1>
        <p className="text-sm text-ink-400">Your operator account — identity, access, and security.</p>
      </header>

      <div className="grid max-w-3xl grid-cols-1 gap-4 lg:grid-cols-2">
        <IdentityCard profile={profile} onUpdated={setProfile} onSignOut={() => { void logout() }} />
        <PermissionsCard profile={profile} />
        <PasswordCard onChanged={() => { void logout() }} />
      </div>
    </div>
  )
}

function IdentityCard({
  profile,
  onUpdated,
  onSignOut,
}: {
  profile: ProfileResponse
  onUpdated: (p: ProfileResponse) => void
  onSignOut: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(profile.full_name ?? '')

  const save = useMutation({
    mutationFn: (full_name: string) =>
      request<ProfileResponse>('/users/me', { method: 'PATCH', body: { full_name } }),
    onSuccess: (updated) => {
      onUpdated(updated)
      setEditing(false)
    },
  })

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-base font-semibold tracking-tight text-ink-900">Account</h2>

      <dl className="grid grid-cols-3 gap-x-4 gap-y-3 text-sm">
        <dt className="text-ink-400">Email</dt>
        <dd className="col-span-2 truncate font-medium text-ink-900">{profile.email ?? '—'}</dd>

        <dt className="self-center text-ink-400">Full name</dt>
        <dd className="col-span-2">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={name}
                autoFocus
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') save.mutate(name.trim())
                  if (e.key === 'Escape') { setName(profile.full_name ?? ''); setEditing(false) }
                }}
                className="min-w-0 flex-1 rounded-md border border-ink-200 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate(name.trim())}
                className="rounded-md bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
              >
                {save.isPending ? '…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setName(profile.full_name ?? ''); setEditing(false) }}
                className="text-xs text-ink-400 hover:text-ink-700"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="truncate text-ink-900">{profile.full_name ?? '—'}</span>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                Edit
              </button>
            </div>
          )}
        </dd>

        <dt className="text-ink-400">Role</dt>
        <dd className="col-span-2">
          <span className="inline-flex items-center rounded bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
            {profile.role}
          </span>
        </dd>

        <dt className="text-ink-400">Status</dt>
        <dd className="col-span-2">
          <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
            profile.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-ink-100 text-ink-500'
          }`}>
            {profile.is_active ? 'Active' : 'Inactive'}
          </span>
          {!profile.is_admin_available && (
            <span className="ml-1.5 inline-flex items-center rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              admin unavailable
            </span>
          )}
        </dd>

        <dt className="text-ink-400">User id</dt>
        <dd className="col-span-2 font-mono text-xs text-ink-500">{profile.id}</dd>
      </dl>

      {save.error instanceof APIError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {save.error.message}
        </div>
      )}

      <button
        type="button"
        onClick={onSignOut}
        className="mt-5 w-full rounded-md border border-ink-200 px-3 py-2 text-sm font-medium text-ink-700 transition hover:border-red-300 hover:text-red-700"
      >
        Sign out
      </button>
    </div>
  )
}

function PermissionsCard({ profile }: { profile: ProfileResponse }) {
  const isWildcard = profile.permissions.includes('*')
  const groups = groupPermissions(profile.permissions.filter((p) => p !== '*'))

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold tracking-tight text-ink-900">Access</h2>
      <p className="mb-3 mt-0.5 text-xs text-ink-400">
        What the <span className="font-medium text-ink-600">{profile.role}</span> role grants you.
      </p>

      {isWildcard ? (
        <div className="rounded-md border border-brand-100 bg-brand-50 px-3 py-2 text-sm text-brand-700">
          ✦ All permissions (admin wildcard)
        </div>
      ) : groups.length === 0 ? (
        <div className="text-sm text-ink-400">No permissions.</div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {groups.map((g) => (
            <div key={g.resource} className="flex flex-wrap items-center gap-1.5">
              <span className="w-32 shrink-0 text-xs font-medium uppercase tracking-wide text-ink-400">{g.resource}</span>
              {g.actions.map((a) => (
                <span key={a} className="rounded bg-ink-50 px-1.5 py-0.5 font-mono text-[11px] text-ink-600">{a}</span>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function PasswordCard({ onChanged }: { onChanged: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const mismatch = confirm.length > 0 && next !== confirm
  const tooShort = next.length > 0 && next.length < 8
  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm

  const change = useMutation({
    mutationFn: () =>
      request<void>('/users/me/password', {
        method: 'POST',
        body: { current_password: current, new_password: next },
      }),
    onSuccess: () => {
      // The backend revokes all refresh tokens — sign out so the operator
      // re-authenticates cleanly with the new password.
      onChanged()
    },
  })

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm lg:col-span-2">
      <h2 className="text-base font-semibold tracking-tight text-ink-900">Change password</h2>
      <p className="mb-3 mt-0.5 text-xs text-ink-400">
        You’ll be signed out and need to sign in again with the new password.
      </p>

      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (canSubmit) change.mutate()
        }}
      >
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="Current password"
          autoComplete="current-password"
          className="rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
        <input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="New password (min 8)"
          autoComplete="new-password"
          className="rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password"
          autoComplete="new-password"
          className="rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
        <div className="sm:col-span-3 flex items-center gap-3">
          <button
            type="submit"
            disabled={!canSubmit || change.isPending}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {change.isPending ? 'Changing…' : 'Change password'}
          </button>
          {tooShort && <span className="text-xs text-amber-600">At least 8 characters.</span>}
          {mismatch && <span className="text-xs text-red-600">Passwords don’t match.</span>}
          {change.error instanceof APIError && <span className="text-xs text-red-600">{change.error.message}</span>}
        </div>
      </form>
    </div>
  )
}
